/**
 * React Query hooks for activities
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@lib/api';
import { useAuth } from '@lib/auth';
import type { ActivityType, TodaySummary, LogActivityResponse, Activity } from '@lib/types';
import { goalKeys } from './useGoals';
import { badgeKeys } from './useBadges';
import { challengeKeys } from './useChallenges';
import { messageKeys } from './useMessages';
import { toast } from '@stores/toast.store';

// Query keys
export const activityKeys = {
  all: ['activities'] as const,
  types: () => [...activityKeys.all, 'types'] as const,
  today: () => [...activityKeys.all, 'today'] as const,
  history: (filters?: { page?: number; startDate?: string; endDate?: string; typeId?: string }) =>
    [...activityKeys.all, 'history', filters] as const,
};

// Fetch activity types
export function useActivityTypes() {
  const { isAuthenticated, isLoading } = useAuth();
  
  return useQuery({
    queryKey: activityKeys.types(),
    queryFn: () => api.get<ActivityType[]>('/api/activities/types'),
    staleTime: 1000 * 60 * 60, // 1 hour - types rarely change
    enabled: isAuthenticated && !isLoading, // Only fetch when authenticated
  });
}

// Fetch today's summary
export function useTodaySummary() {
  const { isAuthenticated, isLoading } = useAuth();
  
  return useQuery({
    queryKey: activityKeys.today(),
    queryFn: () => api.get<TodaySummary>('/api/activities/today'),
    staleTime: 1000 * 30, // 30 seconds
    refetchInterval: 1000 * 60, // Refetch every minute
    enabled: isAuthenticated && !isLoading, // Only fetch when authenticated
  });
}

// Log activity mutation
export function useLogActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: { activityTypeId: string; quantity?: number; notes?: string; linkedinMessageId?: string; guestId?: string }) =>
      api.post<LogActivityResponse>('/api/activities', data),
    onSuccess: (data) => {
      // Invalidate today's summary to refetch
      queryClient.invalidateQueries({ queryKey: activityKeys.today() });
      queryClient.invalidateQueries({ queryKey: activityKeys.history() });
      // Invalidate goals to update progress
      queryClient.invalidateQueries({ queryKey: goalKeys.current() });
      // Invalidate badges (user may have earned new ones)
      queryClient.invalidateQueries({ queryKey: badgeKeys.list() });
      queryClient.invalidateQueries({ queryKey: badgeKeys.recent() });
      // Invalidate challenges
      queryClient.invalidateQueries({ queryKey: challengeKeys.list() });
      // Invalidate message stats (if a message was linked)
      queryClient.invalidateQueries({ queryKey: messageKeys.stats() });

      // Show toast for goal completion (no bonus points)
      if (data.goalCompleted) {
        toast.success('Goal Complete!', 'Weekly goal achieved!');
      }

      // Show toast for challenge completion
      if (data.challengeCompleted && data.challengeBonusPoints) {
        toast.success('Challenge Complete!', `+${data.challengeBonusPoints} bonus points earned`);
      }

      // Show toast for badges earned
      if (data.badgesEarned && data.badgesEarned.length > 0) {
        for (const badge of data.badgesEarned) {
          toast.success(`Badge Earned: ${badge.name}`, `+${badge.bonusPoints} bonus points`);
        }
      }
    },
  });
}

// Delete activity mutation
export function useDeleteActivity() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (activityId: string) =>
      api.delete<{ deleted: boolean; activityId: string; pointsRemoved: number; quantityRemoved: number }>(
        `/api/activities/${activityId}`
      ),
    onSuccess: () => {
      // Invalidate all activity-related queries
      queryClient.invalidateQueries({ queryKey: activityKeys.today() });
      queryClient.invalidateQueries({ queryKey: activityKeys.history() });
      // Invalidate goals and challenges (quantities may have changed)
      queryClient.invalidateQueries({ queryKey: goalKeys.current() });
      queryClient.invalidateQueries({ queryKey: challengeKeys.list() });
    },
  });
}

// Fetch activity history
interface HistoryFilters {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  typeId?: string;
}

interface HistoryResponse {
  activities: Activity[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export function useActivityHistory(filters: HistoryFilters = {}) {
  const { isAuthenticated, isLoading } = useAuth();
  
  const params = new URLSearchParams();
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.startDate) params.set('startDate', filters.startDate);
  if (filters.endDate) params.set('endDate', filters.endDate);
  if (filters.typeId) params.set('typeId', filters.typeId);

  const queryString = params.toString();
  const url = `/api/activities${queryString ? `?${queryString}` : ''}`;

  return useQuery({
    queryKey: activityKeys.history(filters),
    queryFn: () => api.get<HistoryResponse>(url),
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
    enabled: isAuthenticated && !isLoading,
  });
}
